import apiService from './api';
import { config } from '../config';

import {
  LoginCredentials,
  GoogleLoginCredentials,
  LoginResponse,
  Quest,
  QuestsResponse,
  UserProfile,
  QuestDetailsResponse,
  Module,
  Post,
  UploadResponse,
  MultipleUploadResponse,
  Coach,
  CreateCoachRequest,
  CreateCoachResponse,
  GetCoachesResponse,
  TagsResponse,
  DailyDCModule,
  DateWiseDCModule,
  DCChallengePost,
} from '../types/auth';

interface UpdateModuleResponse {
  message: string;
  module_id: string;
}

class AuthService {
  private api: typeof apiService;

  constructor() {
    this.api = apiService;
  }

  async uploadSingleFile(formData: FormData): Promise<UploadResponse> {
    try {
      console.log('Uploading single file...');
      const response = await this.api.postFormData<UploadResponse>(
        '/upload/single',
        formData
      );
      console.log('Single file upload response:', response);
      if (!response.data) {
        throw new Error('Invalid upload response');
      }
      return response.data;
    } catch (error) {
      console.error('Error uploading single file:', error);
      throw error;
    }
  }

  async uploadMultipleFiles(
    formData: FormData
  ): Promise<MultipleUploadResponse> {
    try {
      console.log('Uploading multiple files...');
      const response = await this.api.postFormData<MultipleUploadResponse>(
        '/upload/multiple',
        formData
      );
      console.log('Multiple files upload response:', response);
      if (!response.data) {
        throw new Error('Invalid upload response');
      }
      return response.data;
    } catch (error) {
      console.error('Error uploading multiple files:', error);
      throw error;
    }
  }

  async convertToWebp(formData: FormData): Promise<Blob> {
    try {
      console.log('Converting image to WebP...');
      const token = localStorage.getItem('authToken');
      const url = `${config.API_BASE_URL}/upload/convert-to-webp`;

      const headers: Record<string, string> = {
        accept: 'application/json',
      };

      // Add dynamic Origin and Referer headers based on current location
      if (typeof window !== 'undefined') {
        headers['Origin'] = window.location.origin;
        headers['Referer'] = window.location.href;
      }

      if (token) {
        headers['x-auth-token'] = token;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      console.log('WebP conversion response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      console.log('WebP conversion successful, blob size:', blob.size);
      return blob;
    } catch (error) {
      console.error('Error converting to WebP:', error);
      throw error;
    }
  }

  async uploadVideo(formData: FormData): Promise<UploadResponse> {
    try {
      console.log('Uploading video...');
      const response = await this.api.postFormData<UploadResponse>(
        '/upload/single',
        formData
      );
      console.log('Video upload response:', response);
      if (!response.data) {
        throw new Error('Invalid upload response');
      }
      return response.data;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      console.log('Attempting login with credentials:', credentials);
      const response = await this.api.post<LoginResponse>(
        '/signup/signin-email',
        credentials
      );
      console.log('Login response received:', response);
      if (!response.data) {
        throw new Error('Invalid login response');
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async loginWithGoogle(idToken: string): Promise<LoginResponse> {
    try {
      console.log('Attempting Google login with id_token');
      const credentials: GoogleLoginCredentials = { id_token: idToken };
      const response = await this.api.post<LoginResponse>(
        '/signup/signin-google',
        credentials
      );
      console.log('Google login response received:', response);
      if (!response.data) {
        throw new Error('Invalid Google login response');
      }
      return response.data;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  async getProfile(): Promise<UserProfile | null> {
    try {
      console.log('Fetching user profile...');
      const response = await this.api.get<UserProfile>('/profile/');
      console.log('Profile response:', response);
      return response.data || null;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  }

  async getQuests(): Promise<QuestsResponse> {
    try {
      console.log('Fetching quests...');
      const response = await this.api.get<QuestsResponse>('/quests/');
      console.log('Quests response:', response);
      if (!response.data) {
        throw new Error('Invalid quests response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching quests:', error);
      throw error;
    }
  }

  async getTags(): Promise<TagsResponse> {
    try {
      console.log('Fetching tags...');
      const response = await this.api.get<TagsResponse>('/tags/');
      console.log('Tags response:', response);
      if (!response.data) {
        throw new Error('Invalid tags response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  async updateQuest(
    questId: string,
    questData: Partial<Quest>
  ): Promise<Quest> {
    try {
      console.log('Updating quest:', questId);
      console.log('Quest data:', questData);

      // Prepare the request data, ensuring all fields are properly handled
      const requestData = {
        title: questData.title || '',
        subtitle: questData.subtitle || '',
        description: questData.description || '',
        image_url: questData.image_url || '',
        video_url: questData.video_url || '',
        thumb_url: questData.thumb_url || '',
        marketing_url: questData.marketing_url || '',
        subscription_image: questData.subscription_image || '',
        subscription_title: questData.subscription_title || '',
        subscription_subtitle: questData.subscription_subtitle || '',
        share_image_url: questData.share_image_url || '',
        share_text: questData.share_text || '',
        duration: questData.duration || 0,
        no_of_modules: questData.no_of_modules || 0,
        no_of_exercises: questData.no_of_exercises || 0,
        no_of_videos: questData.no_of_videos || 0,
        no_of_audios: questData.no_of_audios || 0,
        total_no_of_posts: questData.total_no_of_posts || 0,
        success_image_url: questData.success_image_url || '',
        title_color: questData.title_color || '',
        landscape_image_url: questData.landscape_image_url || '',
        portrait_image_url: questData.portrait_image_url || '',
        is_public:
          questData.is_public !== undefined ? questData.is_public : true,
        tags: questData.tags || [],
        category: questData.category || '',
      };

      console.log('Request data being sent:', requestData);
      console.log(
        'Tags being sent:',
        requestData.tags,
        'Type:',
        Array.isArray(requestData.tags)
      );
      console.log('Subscription fields in request:', {
        subscription_image: requestData.subscription_image,
        subscription_title: requestData.subscription_title,
        subscription_subtitle: requestData.subscription_subtitle,
      });

      const response = await this.api.put<Quest>(
        `/quests/${questId}`,
        requestData
      );
      console.log('Update response:', response);
      console.log('Response data:', response.data);

      if (!response.data) {
        throw new Error('Invalid update response');
      }

      // Ensure the response has all required fields
      const updatedQuest: Quest = {
        id: response.data.id || questId,
        title: response.data.title || requestData.title,
        subtitle: response.data.subtitle || requestData.subtitle,
        description: response.data.description || requestData.description,
        image_url: response.data.image_url || requestData.image_url,
        video_url: response.data.video_url || requestData.video_url,
        thumb_url: response.data.thumb_url || requestData.thumb_url,
        marketing_url: response.data.marketing_url || requestData.marketing_url,
        subscription_image:
          response.data.subscription_image || requestData.subscription_image,
        subscription_title:
          response.data.subscription_title || requestData.subscription_title,
        subscription_subtitle:
          response.data.subscription_subtitle ||
          requestData.subscription_subtitle,
        share_image_url:
          response.data.share_image_url ||
          (requestData as any).share_image_url ||
          '',
        share_text:
          response.data.share_text || (requestData as any).share_text || '',
        duration: response.data.duration || requestData.duration,
        no_of_modules: response.data.no_of_modules || requestData.no_of_modules,
        no_of_exercises:
          response.data.no_of_exercises ||
          (requestData as any).no_of_exercises ||
          0,
        no_of_videos:
          response.data.no_of_videos || (requestData as any).no_of_videos || 0,
        no_of_audios:
          response.data.no_of_audios || (requestData as any).no_of_audios || 0,
        total_no_of_posts:
          response.data.total_no_of_posts ||
          (requestData as any).total_no_of_posts ||
          0,
        success_image_url:
          response.data.success_image_url ||
          (requestData as any).success_image_url ||
          '',
        title_color:
          response.data.title_color || (requestData as any).title_color || '',
        landscape_image_url:
          response.data.landscape_image_url ||
          (requestData as any).landscape_image_url ||
          '',
        portrait_image_url:
          response.data.portrait_image_url ||
          (requestData as any).portrait_image_url ||
          '',
        creator_id: response.data.creator_id || '',
        is_public:
          response.data.is_public !== undefined
            ? response.data.is_public
            : requestData.is_public,
        tags: response.data.tags || requestData.tags,
        category: response.data.category || requestData.category,
      };

      console.log('Processed updated quest:', updatedQuest);

      // Push data to SQS queue for quest update
      try {
        await this.pushToSQS({
          post_id: updatedQuest.id,
          video_url: updatedQuest.video_url || '',
          image_url: updatedQuest.image_url || '',
          thumbnail: updatedQuest.thumb_url || '',
          entity_type: 'quest',
        });
        console.log('Updated quest data pushed to SQS successfully');
      } catch (sqsError) {
        console.error(
          'Failed to push updated quest data to SQS (non-critical):',
          sqsError
        );
        // Don't throw error here as SQS push failure shouldn't break quest update
      }

      return updatedQuest;
    } catch (error) {
      console.error('Error updating quest:', error);
      throw error;
    }
  }

  async createQuest(
    questData: Omit<Quest, 'id' | 'creator_id'>
  ): Promise<Quest> {
    try {
      console.log('Creating quest with data:', questData);
      console.log(
        'Tags in create quest:',
        questData.tags,
        'Type:',
        Array.isArray(questData.tags)
      );
      const response = await this.api.post<{
        message: string;
        quest_id: string;
      }>('/quests/', questData);
      console.log('Create quest response:', response);

      if (!response.data || !response.data.quest_id) {
        throw new Error('Invalid create response');
      }

      // Since the API only returns quest_id, we need to construct the quest object
      // We'll use the request data and the returned quest_id
      const createdQuest: Quest = {
        id: response.data.quest_id,
        title: questData.title,
        subtitle: questData.subtitle || '',
        description: questData.description || '',
        image_url: questData.image_url,
        video_url: questData.video_url || '',
        thumb_url: questData.thumb_url || '',
        marketing_url: questData.marketing_url || '',
        subscription_image: questData.subscription_image || '',
        subscription_title: questData.subscription_title || '',
        subscription_subtitle: questData.subscription_subtitle || '',
        share_image_url: questData.share_image_url || '',
        share_text: questData.share_text || '',
        duration: questData.duration,
        no_of_modules: questData.no_of_modules,
        no_of_exercises: questData.no_of_exercises || 0,
        no_of_videos: questData.no_of_videos || 0,
        no_of_audios: questData.no_of_audios || 0,
        total_no_of_posts: questData.total_no_of_posts || 0,
        success_image_url: questData.success_image_url || '',
        title_color: questData.title_color || '',
        landscape_image_url: questData.landscape_image_url || '',
        portrait_image_url: questData.portrait_image_url || '',
        creator_id: '', // This will be set by the backend
        is_public: questData.is_public,
        tags: questData.tags || [],
        category: questData.category || '',
      };

      console.log('Constructed quest object:', createdQuest);

      // Push data to SQS queue for quest creation
      try {
        await this.pushToSQS({
          post_id: createdQuest.id,
          video_url: createdQuest.video_url || '',
          image_url: createdQuest.image_url || '',
          thumbnail: createdQuest.thumb_url || '',
          entity_type: 'quest',
        });
        console.log('Quest data pushed to SQS successfully');
      } catch (sqsError) {
        console.error(
          'Failed to push quest data to SQS (non-critical):',
          sqsError
        );
        // Don't throw error here as SQS push failure shouldn't break quest creation
      }

      return createdQuest;
    } catch (error) {
      console.error('Error creating quest:', error);
      throw error;
    }
  }

  async deleteQuest(questId: string): Promise<void> {
    try {
      console.log('Deleting quest:', questId);
      await this.api.delete(`/quests/${questId}`);
      console.log('Quest deleted successfully');
    } catch (error) {
      console.error('Error deleting quest:', error);
      throw error;
    }
  }

  async getQuestDetails(questId: string): Promise<QuestDetailsResponse> {
    try {
      console.log('Fetching quest details:', questId);
      const response = await this.api.get<QuestDetailsResponse>(
        `/quests/get_quest_all_data/${questId}`
      );
      console.log('Quest details response:', response);
      if (!response.data) {
        throw new Error('Invalid quest details response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching quest details:', error);
      throw error;
    }
  }

  async createModule(
    moduleData: Omit<Module, 'id' | 'creator_id'>
  ): Promise<UpdateModuleResponse> {
    try {
      console.log('Creating module with data:', moduleData);

      const requestData = {
        title: moduleData.title || '',
        subtitle: moduleData.subtitle || '',
        description: moduleData.description || '',
        image_url: moduleData.image_url || '',
        video_url: moduleData.video_url || '',
        duration: moduleData.duration || 0,
        no_of_contents: moduleData.no_of_contents || 0,
        quest_id: moduleData.quest_id || '',
        module_order: moduleData.module_order || 0,
      };

      console.log('Request data being sent:', requestData);

      const response = await this.api.post<UpdateModuleResponse>(
        '/modules/',
        requestData
      );
      console.log('Create module response:', response);
      if (!response.data) {
        throw new Error('Invalid create module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating module:', error);
      throw error;
    }
  }

  async updateModule(
    moduleId: string,
    moduleData: Partial<Module>
  ): Promise<UpdateModuleResponse> {
    try {
      console.log('Updating module:', moduleId);
      console.log('Module data:', moduleData);

      const requestData = {
        title: moduleData.title || '',
        subtitle: moduleData.subtitle || '',
        description: moduleData.description || '',
        image_url: moduleData.image_url || '',
        video_url: moduleData.video_url || '',
        duration: moduleData.duration || 0,
        no_of_contents: moduleData.no_of_contents || 0,
        quest_id: moduleData.quest_id || '',
        module_order: moduleData.module_order || 0,
      };

      console.log('Request data being sent:', requestData);

      const response = await this.api.put<UpdateModuleResponse>(
        `/modules/${moduleId}`,
        requestData
      );
      console.log('Update module response:', response);
      if (!response.data) {
        throw new Error('Invalid update module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating module:', error);
      throw error;
    }
  }

  async updatePost(postId: string, postData: Partial<Post>): Promise<Post> {
    try {
      console.log('Updating post:', postId);
      console.log('Post data:', postData);
      console.log(
        'Tags in update post:',
        postData.tags,
        'Type:',
        Array.isArray(postData.tags)
      );

      const requestData = {
        attributes: postData.attributes || {},
        text: postData.text || '',
        script: postData.script || '',
        first_question: postData.first_question || '',
        image_url: postData.image_url || '',
        link_url: postData.link_url || '',
        thumbnail: postData.thumbnail || '',
        video_url: postData.video_url || '',
        youtube_url: postData.youtube_url || '',
        audio_url: postData.audio_url || '',
        lrc_url: postData.lrc_url || '',
        post_language: postData.post_language || '',
        post_type: postData.post_type || '',
        post_title: postData.post_title || '',
        post_subtitle: postData.post_subtitle || '',
        tags: postData.tags || [],
        category: postData.category || '',
        // Include MCQSC/MCQMC specific fields
        ...(postData.question && { question: postData.question }),
        ...(postData.options && { options: postData.options }),
        ...(postData.answers && { answers: postData.answers }),
      };

      console.log('Request data being sent:', requestData);

      const response = await this.api.put<Post>(
        `/posts/${postId}`,
        requestData
      );
      console.log('Update post response:', response);
      if (!response.data) {
        throw new Error('Invalid update post response');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  }

  async createPost(
    postData: Omit<Post, 'id' | 'user_id' | 'created_at' | 'update_at'> & {
      module_id?: string;
    }
  ): Promise<Post> {
    try {
      console.log('Creating post with data:', postData);
      console.log(
        'Tags in create post:',
        postData.tags,
        'Type:',
        Array.isArray(postData.tags)
      );

      const requestData = {
        attributes: postData.attributes || {},
        text: postData.text || '',
        script: postData.script || '',
        first_question: postData.first_question || '',
        image_url: postData.image_url || '',
        link_url: postData.link_url || '',
        thumbnail: postData.thumbnail || '',
        video_url: postData.video_url || '',
        youtube_url: postData.youtube_url || '',
        audio_url: postData.audio_url || '',
        lrc_url: postData.lrc_url || '',
        post_language: postData.post_language || '',
        post_type: postData.post_type || '',
        post_title: postData.post_title || '',
        post_subtitle: postData.post_subtitle || '',
        tags: postData.tags || [],
        category: postData.category || '',
        ...(postData.module_id && { module_id: postData.module_id }),
        // Include MCQSC/MCQMC specific fields
        ...(postData.question && { question: postData.question }),
        ...(postData.options && { options: postData.options }),
        ...(postData.answers && { answers: postData.answers }),
      };

      console.log('Request data being sent:', requestData);

      const response = await this.api.post<Post>('/posts/', requestData);
      console.log('Create post response:', response);
      if (!response.data) {
        throw new Error('Invalid create post response');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  async getPostMeta(postId: string): Promise<Post> {
    try {
      console.log('Fetching post metadata:', postId);
      const response = await this.api.get<Post>(`/posts/${postId}`);
      console.log('Get post meta response:', response);
      if (!response.data) {
        throw new Error('Invalid get post meta response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching post metadata:', error);
      throw error;
    }
  }

  async createPostQuestModuleMapper(
    postMetaId: string,
    questId: string,
    moduleId: string,
    postOrder: number
  ): Promise<any> {
    try {
      console.log('Creating post-quest-module mapper:', {
        postMetaId,
        questId,
        moduleId,
        postOrder,
      });

      const requestData = {
        post_meta_id: postMetaId,
        quest_id: questId,
        module_id: moduleId,
        post_order: postOrder,
      };

      console.log('Request data being sent:', requestData);

      const response = await this.api.post<any>(
        '/post-quest-module-mapper/',
        requestData
      );
      console.log('Create mapper response:', response);
      if (!response.data) {
        throw new Error('Invalid create mapper response');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating post-quest-module mapper:', error);
      throw error;
    }
  }

  async updatePostQuestModuleMapper(
    postMetaId: string,
    questId: string,
    moduleId: string,
    postOrder: number
  ): Promise<any> {
    try {
      console.log('Updating post-quest-module mapper:', {
        postMetaId,
        questId,
        moduleId,
        postOrder,
      });

      const requestData = {
        post_meta_id: postMetaId,
        quest_id: questId,
        module_id: moduleId,
        post_order: postOrder,
      };

      console.log('Request data being sent:', requestData);

      const response = await this.api.put<any>(
        '/post-quest-module-mapper/',
        requestData
      );
      console.log('Update mapper response:', response);
      if (!response.data) {
        throw new Error('Invalid update mapper response');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating post-quest-module mapper:', error);
      throw error;
    }
  }

  async pushToSQS(postData: {
    post_id: string;
    video_url: string;
    image_url?: string;
    thumbnail: string;
    audio_url?: string;
    lrc_url?: string;
    entity_type?: string;
  }): Promise<void> {
    try {
      console.log('pushToSQS called with:', postData);

      // Filter out URLs that contain "_compressed" substring and check if field has value
      const shouldIncludeUrl = (url: string): boolean => {
        return !!url && !url.includes('_compressed');
      };

      const filteredData: Record<string, string> = {};

      // Only include fields that are not nil or empty
      if (postData.post_id && postData.post_id.trim()) {
        filteredData.post_id = postData.post_id;
      }

      // For feed posts, only send specific fields as per requirements
      if (postData.entity_type === 'feed_post') {
        if (shouldIncludeUrl(postData.video_url)) {
          filteredData.video_url = postData.video_url;
        }
        if (shouldIncludeUrl(postData.thumbnail)) {
          filteredData.thumbnail = postData.thumbnail;
        }
        filteredData.entity_type = 'feed_post';
      } else if (postData.entity_type === 'challenge_post') {
        // For challenge posts, send post_id, video_url, image_url, thumbnail, and entity_type
        if (postData.video_url && shouldIncludeUrl(postData.video_url)) {
          filteredData.video_url = postData.video_url;
        }
        if (postData.image_url && shouldIncludeUrl(postData.image_url)) {
          filteredData.image_url = postData.image_url;
        }
        if (postData.thumbnail && shouldIncludeUrl(postData.thumbnail)) {
          filteredData.thumbnail = postData.thumbnail;
        }
        filteredData.entity_type = 'challenge_post';
      } else {
        // For quest posts and other post types, include all fields
        if (shouldIncludeUrl(postData.video_url)) {
          filteredData.video_url = postData.video_url;
        }

        if (postData.image_url && shouldIncludeUrl(postData.image_url)) {
          filteredData.image_url = postData.image_url;
        }

        if (shouldIncludeUrl(postData.thumbnail)) {
          filteredData.thumbnail = postData.thumbnail;
        }

        if (postData.audio_url && shouldIncludeUrl(postData.audio_url)) {
          filteredData.audio_url = postData.audio_url;
        }

        if (postData.lrc_url && shouldIncludeUrl(postData.lrc_url)) {
          filteredData.lrc_url = postData.lrc_url;
        }

        // Set entity_type for quest posts
        if (postData.entity_type === 'quest_post') {
          filteredData.entity_type = 'quest_post';
        }
      }

      console.log('Final filtered data for SQS:', filteredData);
      console.log(
        'Number of fields in payload:',
        Object.keys(filteredData).length
      );

      const response = await this.api.post<any>(
        '/upload/sqs-push-data',
        filteredData
      );
      console.log('SQS push response:', response);
    } catch (error) {
      console.error('Error pushing data to SQS:', error);
      throw error;
    }
  }

  async createCoach(
    coachData: CreateCoachRequest
  ): Promise<CreateCoachResponse> {
    try {
      console.log('Creating coach with data:', coachData);

      const response = await this.api.post<CreateCoachResponse>(
        '/ai-coaches/',
        coachData
      );
      console.log('Create coach response:', response);
      if (!response.data) {
        throw new Error('Invalid create coach response');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating coach:', error);
      throw error;
    }
  }

  async updateCoach(
    coachId: string,
    coachData: Partial<CreateCoachRequest>
  ): Promise<CreateCoachResponse> {
    try {
      console.log('Updating coach with data:', coachData);

      const response = await this.api.put<CreateCoachResponse>(
        `/ai-coaches/${coachId}`,
        coachData
      );
      console.log('Update coach response:', response);
      if (!response.data) {
        throw new Error('Invalid update coach response');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating coach:', error);
      throw error;
    }
  }

  async getCoachesByQuestId(
    questId: string,
    skip: number = 0,
    limit: number = 100,
    activeOnly: boolean = true
  ): Promise<GetCoachesResponse> {
    try {
      console.log('Fetching coaches for quest:', questId);

      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        active_only: activeOnly.toString(),
      });

      const response = await this.api.get<GetCoachesResponse>(
        `/ai-coaches/quest/${questId}?${params.toString()}`
      );
      console.log('Get coaches response:', response);
      if (!response.data) {
        throw new Error('Invalid get coaches response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching coaches:', error);
      throw error;
    }
  }

  async getCoachesByChallengeId(
    challengeId: string,
    skip: number = 0,
    limit: number = 100,
    activeOnly: boolean = true
  ): Promise<GetCoachesResponse> {
    try {
      console.log('Fetching coaches for challenge:', challengeId);

      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        active_only: activeOnly.toString(),
      });

      const response = await this.api.get<GetCoachesResponse>(
        `/ai-coaches/quest/${challengeId}?${params.toString()}`
      );
      console.log('Get coaches response:', response);
      if (!response.data) {
        throw new Error('Invalid get coaches response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching coaches:', error);
      throw error;
    }
  }

  async getCoachById(coachId: string): Promise<Coach> {
    try {
      console.log('Fetching coach by ID:', coachId);
      const response = await this.api.get<Coach>(`/ai-coaches/${coachId}`);
      console.log('Get coach response:', response);
      if (!response.data) {
        throw new Error('Invalid get coach response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching coach:', error);
      throw error;
    }
  }

  async deletePostQuestModuleMapper(
    moduleId: string,
    postId: string
  ): Promise<{ message: string }> {
    try {
      console.log('Deleting post-quest-module mapper:', { moduleId, postId });
      const response = await this.api.delete<{ message: string }>(
        `/post-quest-module-mapper/${moduleId}/${postId}`
      );
      console.log('Delete mapper response:', response);
      if (!response.data) {
        throw new Error('Invalid delete mapper response');
      }
      return response.data;
    } catch (error) {
      console.error('Error deleting post-quest-module mapper:', error);
      throw error;
    }
  }

  async createDailyModule(moduleData: {
    dc_id: string;
    title: string;
    medal_share_image_url: string;
    medal_image_url: string;
    medal_joy_audio_url: string;
    medal_title: string;
    medal_subtitle: string;
    medal_share_text: string;
    medal_share_url: string;
    ai_coach_context: string;
    children_experiment_module_id?: string[];
    start_color?: string;
    gold_medal_image_url?: string;
    silver_medal_image_url?: string;
    bronze_medal_image_url?: string;
    total_available_points?: string;
  }): Promise<any> {
    try {
      console.log('Creating daily module with data:', moduleData);
      const payload = { ...moduleData, entity_id: moduleData.dc_id };
      delete (payload as any).dc_id;
      const response = await this.api.post<any>('/daily-module/', payload);
      console.log('Create daily module response:', response);
      if (!response.data) {
        throw new Error('Invalid create daily module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating daily module:', error);
      throw error;
    }
  }

  async updateDailyModule(moduleData: {
    module_id: string;
    title: string;
    medal_share_image_url: string;
    medal_image_url: string;
    medal_joy_audio_url: string;
    medal_title: string;
    medal_subtitle: string;
    medal_share_text: string;
    medal_share_url: string;
    ai_coach_context: string;
    children_experiment_module_id?: string[];
    start_color?: string;
    gold_medal_image_url?: string;
    silver_medal_image_url?: string;
    bronze_medal_image_url?: string;
    total_available_points?: string;
  }): Promise<{ message: string }> {
    try {
      console.log('Updating daily module with data:', moduleData);
      const { module_id, ...payload } = moduleData;
      const response = await this.api.put<{ message: string }>(
        `/daily-module/${module_id}`,
        payload
      );
      console.log('Update daily module response:', response);
      if (!response.data) {
        throw new Error('Invalid update daily module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating daily module:', error);
      throw error;
    }
  }

  async createDateWiseModule(moduleData: {
    dc_id: string;
    event_date: string;
    title: string;
  }): Promise<any> {
    try {
      console.log('Creating date wise module with data:', moduleData);
      const payload = { ...moduleData, entity_id: moduleData.dc_id };
      delete (payload as any).dc_id;
      const response = await this.api.post<any>('/date-wise-module/', payload);
      console.log('Create date wise module response:', response);
      if (!response.data) {
        throw new Error('Invalid create date wise module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating date wise module:', error);
      throw error;
    }
  }

  async updateDateWiseModule(moduleData: {
    module_id: string;
    event_date: string;
    title: string;
  }): Promise<{ message: string }> {
    try {
      console.log('Updating date wise module with data:', moduleData);
      const response = await this.api.put<{ message: string }>(
        `/date-wise-module/${moduleData.module_id}`,
        { title: moduleData.title, event_date: moduleData.event_date }
      );
      console.log('Update date wise module response:', response);
      if (!response.data) {
        throw new Error('Invalid update date wise module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating date wise module:', error);
      throw error;
    }
  }

  async getDailyDCModules(dcId: string): Promise<DailyDCModule[]> {
    try {
      console.log('Fetching daily DC modules for:', dcId);
      const response = await this.api.get<DailyDCModule[]>(
        `/daily-module/${dcId}`
      );
      console.log('Get daily DC modules response:', response);
      if (!response.data) {
        throw new Error('Invalid get daily DC modules response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching daily DC modules:', error);
      throw error;
    }
  }

  async getDateWiseDCModules(dcId: string): Promise<DateWiseDCModule[]> {
    try {
      console.log('Fetching date wise DC modules for:', dcId);
      const response = await this.api.get<DateWiseDCModule[]>(
        `/date-wise-module/${dcId}`
      );
      console.log('Get date wise DC modules response:', response);
      if (!response.data) {
        throw new Error('Invalid get date wise DC modules response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching date wise DC modules:', error);
      throw error;
    }
  }

  async createDCChallengePost(
    postData: Omit<DCChallengePost, 'id' | 'created_at' | 'updated_at'>
  ): Promise<DCChallengePost> {
    try {
      console.log('Creating DC challenge post with data:', postData);
      const response = await this.api.post<DCChallengePost>('/post/', postData);
      console.log('Create DC challenge post response:', response);
      if (!response.data) {
        throw new Error('Invalid create DC challenge post response');
      }

      // Push data to SQS queue
      try {
        await this.pushToSQS({
          post_id: response.data.id || '',
          video_url: response.data.video_url || '',
          image_url: response.data.image_url || '',
          thumbnail: response.data.thumbnail || '',
          entity_type: 'challenge_post',
        });
        console.log('DC challenge post data pushed to SQS successfully');
      } catch (sqsError) {
        console.error(
          'Failed to push DC post data to SQS (non-critical):',
          sqsError
        );
      }

      return response.data;
    } catch (error) {
      console.error('Error creating DC challenge post:', error);
      throw error;
    }
  }

  async updateDCChallengePost(
    postId: string,
    postData: Omit<DCChallengePost, 'id' | 'created_at' | 'updated_at'>
  ): Promise<DCChallengePost> {
    try {
      console.log('Updating DC challenge post with data:', postData);
      const response = await this.api.put<DCChallengePost>(
        `/post/${postId}`,
        postData
      );
      console.log('Update DC challenge post response:', response);
      if (!response.data) {
        throw new Error('Invalid update DC challenge post response');
      }

      // Push data to SQS queue
      try {
        await this.pushToSQS({
          post_id: response.data.id || '',
          video_url: response.data.video_url || '',
          image_url: response.data.image_url || '',
          thumbnail: response.data.thumbnail || '',
          entity_type: 'challenge_post',
        });
        console.log(
          'Updated DC challenge post data pushed to SQS successfully'
        );
      } catch (sqsError) {
        console.error(
          'Failed to push updated DC post data to SQS (non-critical):',
          sqsError
        );
      }

      return response.data;
    } catch (error) {
      console.error('Error updating DC challenge post:', error);
      throw error;
    }
  }

  async createDCModulePostMapper(mapperData: {
    module_id: string;
    post_id: string;
    entity_id: string;
  }): Promise<any> {
    try {
      console.log('Creating DC module post mapper with data:', mapperData);
      const response = await this.api.post<any>(
        '/module-post-mapper/',
        mapperData
      );
      console.log('Create DC module post mapper response:', response);
      if (!response.data) {
        throw new Error('Invalid create DC module post mapper response');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating DC module post mapper:', error);
      throw error;
    }
  }

  async getDCModulePosts(moduleId: string): Promise<{ posts: any[] }> {
    try {
      console.log('Fetching DC module posts for:', moduleId);
      const response = await this.api.get<{ posts: any[] }>(
        `/post/module/${moduleId}`
      );
      console.log('Get DC module posts response:', response);
      if (!response.data) {
        throw new Error('Invalid get DC module posts response');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching DC module posts:', error);
      throw error;
    }
  }

  async deleteDCModulePost(
    moduleId: string,
    postId: string
  ): Promise<{ message: string }> {
    try {
      console.log('Deleting DC module post:', { moduleId, postId });
      const response = await this.api.delete<{ message: string }>(
        `/post/module/${moduleId}/post/${postId}`
      );
      console.log('Delete DC module post response:', response);
      if (!response.data) {
        throw new Error('Invalid delete DC module post response');
      }
      return response.data;
    } catch (error) {
      console.error('Error deleting DC module post:', error);
      throw error;
    }
  }

  clearToken(): void {
    localStorage.removeItem('authToken');
  }

  async logout(): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        // If no token, just clear localStorage
        this.clearToken();
        return;
      }

      console.log('Logging out...');
      const response = await this.api.post('/signup/logout', {});
      console.log('Logout response:', response);

      // Clear the token from localStorage regardless of API response
      this.clearToken();
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if API call fails, clear the token from localStorage
      this.clearToken();
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }

  getAuthData(): { user: UserProfile | null; token: string | null } {
    const token = localStorage.getItem('authToken');
    // Note: In a real app, you might want to decode the token to get user info
    // For now, we'll return null for user since we don't store it in localStorage
    return {
      user: null,
      token,
    };
  }

  setAuthData(token: string, user: UserProfile): void {
    localStorage.setItem('authToken', token);
    // Note: In a real app, you might want to store user data as well
    // For now, we'll just store the token
  }

  async getAudioUrls(): Promise<any[]> {
    try {
      const response = await this.api.get<any[]>(
        '/joy-audio-url/joy-audio-urls'
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching audio URLs:', error);
      throw error;
    }
  }

  async createAudioUrl(data: {
    language: string;
    purpose: string;
    audio_urls: string[];
  }): Promise<any> {
    try {
      const response = await this.api.post<any>(
        '/joy-audio-url/joy-audio-urls',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating audio URL:', error);
      throw error;
    }
  }

  async deleteDailyChallenge(
    challengeId: string
  ): Promise<{ message: string }> {
    try {
      console.log('Deleting daily challenge:', challengeId);
      const response = await this.api.delete<{ message: string }>(
        `/daily-challenges/delete/${challengeId}`
      );
      console.log('Delete daily challenge response:', response);
      if (!response.data) {
        throw new Error('Invalid delete daily challenge response');
      }
      return response.data;
    } catch (error) {
      console.error('Error deleting daily challenge:', error);
      throw error;
    }
  }

  async deleteMasteryChallenge(
    challengeId: string
  ): Promise<{ message: string }> {
    try {
      console.log('Deleting mastery challenge:', challengeId);
      const response = await this.api.delete<{ message: string }>(
        `/mastery-challenges/delete/${challengeId}`
      );
      console.log('Delete mastery challenge response:', response);
      if (!response.data) {
        throw new Error('Invalid delete mastery challenge response');
      }
      return response.data;
    } catch (error) {
      console.error('Error deleting mastery challenge:', error);
      throw error;
    }
  }

  async deleteDailyModule(moduleId: string): Promise<{ message: string }> {
    try {
      console.log('Deleting daily module:', moduleId);
      const response = await this.api.delete<{ message: string }>(
        `/daily-module/delete/${moduleId}`
      );
      console.log('Delete daily module response:', response);
      if (!response.data) {
        throw new Error('Invalid delete daily module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error deleting daily module:', error);
      throw error;
    }
  }

  async deleteDateWiseModule(moduleId: string): Promise<{ message: string }> {
    try {
      console.log('Deleting date wise module:', moduleId);
      const response = await this.api.delete<{ message: string }>(
        `/date-wise-module/delete/${moduleId}`
      );
      console.log('Delete date wise module response:', response);
      if (!response.data) {
        throw new Error('Invalid delete date wise module response');
      }
      return response.data;
    } catch (error) {
      console.error('Error deleting date wise module:', error);
      throw error;
    }
  }
  async resetDailyChallenge(resetData: {
    phone_number: string;
    dc_id: string;
    day_to_reset_to: number;
    post_id: string;
  }): Promise<any> {
    try {
      console.log('Resetting daily challenge with data:', resetData);
      const params = new URLSearchParams({
        phone_number: resetData.phone_number,
        dc_id: resetData.dc_id,
        day_to_reset_to: resetData.day_to_reset_to.toString(),
        post_id: resetData.post_id,
      });
      const response = await this.api.post<any>(
        `/daily-challenges/internal-reset?${params.toString()}`,
        {}
      );
      console.log('Reset daily challenge response:', response);
      if (!response.data) {
        throw new Error('Invalid reset daily challenge response');
      }
      return response.data;
    } catch (error) {
      console.error('Error resetting daily challenge:', error);
      throw error;
    }
  }

  async updatePostTime(postId: string, timeToUpdate: string): Promise<any> {
    try {
      console.log('Updating post time:', { postId, timeToUpdate });
      const formattedTime = timeToUpdate
        .replace(/\+/g, '%2B')
        .replace(/:/g, '%3A');
      const response = await this.api.put<any>(
        `/post/${postId}/update-time?time_to_update=${formattedTime}`,
        {}
      );
      console.log('Update post time response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating post time:', error);
      throw error;
    }
  }

  async batchUpdatePostOrder(postIds: string[]): Promise<any> {
    try {
      console.log('Batch updating post order:', postIds);
      const response = await this.api.put<any>('/post/batch-update-time', {
        post_ids: postIds,
      });
      console.log('Batch update post order response:', response);
      return response.data;
    } catch (error) {
      console.error('Error batch updating post order:', error);
      throw error;
    }
  }

  async getUserFeedbacks(): Promise<any[]> {
    try {
      console.log('Fetching user feedbacks...');
      const response = await this.api.get<any[]>('/user-feedback/');
      console.log('User feedbacks response:', response);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching user feedbacks:', error);
      throw error;
    }
  }

  async getUserFeedbacksByPhone(phoneNumber: string): Promise<any[]> {
    try {
      console.log('Fetching user feedbacks by phone:', phoneNumber);
      const response = await this.api.get<{ data: any[]; count: number }>(
        `/user-feedback/phone/91${phoneNumber}`
      );
      console.log('User feedbacks by phone response:', response);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching user feedbacks by phone:', error);
      throw error;
    }
  }

  async submitUserFeedback(feedbackData: {
    phone_number: string;
    feedback: string;
    audio_url: string;
  }): Promise<any> {
    try {
      console.log('Submitting user feedback:', feedbackData);
      const payload = {
        phone_no: `91${feedbackData.phone_number}`,
        feed_back: feedbackData.feedback,
        audio_url: feedbackData.audio_url,
      };
      const response = await this.api.post<any>(
        '/user-feedback/create',
        payload
      );
      console.log('Submit feedback response:', response);
      return response.data;
    } catch (error) {
      console.error('Error submitting user feedback:', error);
      throw error;
    }
  }

  async transcribeAudio(audioUrl: string): Promise<{ transcription: string }> {
    try {
      console.log('Transcribing audio:', audioUrl);
      const response = await this.api.post<{ transcription: string }>(
        '/user-feedback/transcribe',
        { audio_url: audioUrl }
      );
      console.log('Transcription response:', response);
      if (!response.data) {
        throw new Error('Invalid transcription response');
      }
      return response.data;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  }

  async createCategory(categoryData: {
    name: string;
    text_color: string;
    image_url: string;
    language: string;
    rank?: number;
  }): Promise<any> {
    try {
      console.log('Creating category:', categoryData);
      const response = await this.api.post<any>('/category/', categoryData);
      console.log('Create category response:', response);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async getCategories(): Promise<any[]> {
    try {
      console.log('Fetching categories...');
      const response = await this.api.get<any[]>('/category/all-categories');
      console.log('Categories response:', response);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async updateCategory(
    categoryId: string,
    categoryData: {
      name?: string;
      text_color?: string;
      image_url?: string;
      rank?: number;
    }
  ): Promise<any> {
    try {
      console.log('Updating category:', categoryId, categoryData);
      const response = await this.api.put<any>(
        `/category/${categoryId}`,
        categoryData
      );
      console.log('Update category response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  async createCategoryTag(tagData: {
    name: string;
    category_id: string;
    image_url: string;
    text_color: string;
    language: string;
  }): Promise<any> {
    try {
      console.log('Creating category tag:', tagData);
      const response = await this.api.post<any>('/tags-v2/', tagData);
      console.log('Create category tag response:', response);
      return response.data;
    } catch (error) {
      console.error('Error creating category tag:', error);
      throw error;
    }
  }

  async getCategoryTags(categoryId: string): Promise<any[]> {
    try {
      console.log('Fetching category tags for:', categoryId);
      const response = await this.api.get<any[]>(
        `/tags-v2/category/${categoryId}`
      );
      console.log('Category tags response:', response);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching category tags:', error);
      throw error;
    }
  }

  async createConstant(data: {
    constant_name: string;
    constant_value: string;
    day: number;
    month: number;
    year: number;
    hour?: number;
    minute?: number;
    is_sent?: boolean;
    type?: string;
  }): Promise<any> {
    try {
      console.log('Creating constant:', data);
      const response = await this.api.post<any>('/constants/', data);
      console.log('Create constant response:', response);
      return response.data;
    } catch (error) {
      console.error('Error creating constant:', error);
      throw error;
    }
  }

  async getConstants(): Promise<
    {
      constant_id: string;
      constant_name: string;
      constant_value: string;
      type?: string;
    }[]
  > {
    try {
      const response = await this.api.get<
        {
          constant_id: string;
          constant_name: string;
          constant_value: string;
          type?: string;
        }[]
      >('/constants/');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching constants:', error);
      throw error;
    }
  }

  async updateModulePoints(moduleId: string): Promise<any> {
    try {
      console.log('Updating module points for:', moduleId);
      const response = await this.api.patch<any>(
        `/experiment-daily-modules/${moduleId}/update-module-points`,
        {}
      );
      console.log('Update module points response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating module points:', error);
      throw error;
    }
  }
}

export default new AuthService();
